import { Typography, Button, Result, Skeleton } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { capitalCase } from 'change-case';
import { Task } from 'packages/lib/api/annotations/contract/task';
import useSWR, { SWRResponse } from 'swr';
import { apiGet } from 'packages/lib/api/annotations/rest';
import { userEmail } from 'packages/lib/auth';
import { Link } from 'react-router-dom';
import { red, purple } from '@ant-design/colors';
import { customDefaultTheme } from 'packages/lib/common-ui/theme';
import { Error404Result } from 'packages/lib/common-ui/error-pages/error-page-404';
import {
  Instruction,
  instructionsRespZod,
} from 'packages/lib/api/annotations/contract/instruction';
import { useTaskGroupsForSampleGroups } from 'packages/task-man/components/sample-group-task-queues/use-tasks-for-instruction';
import { useSampleGroupsForInstruction } from 'packages/task-man/components/sample-group-task-queues/use-sample-groups-for-instruction';
import { QueueCard } from 'packages/task-man/components/sample-group-task-queues/queue-card';
import {
  taskTypeToWorkflowAlias,
  Role,
  AnnotationTool,
} from 'packages/task-man/constants';
import { getAllClassificationData } from 'packages/task-man/service/pickup/task-queue';
import { dispatchErrorToast } from 'packages/lib/common-ui/toast/api-toast';
import { SampleGroup } from 'packages/lib/api/annotations/contract/sample-group';
import {
  AbstractAnnotationStates,
  mapRoleToAnnotationStates,
} from 'packages/task-man/service/state-machine';
import { TaskGroup } from 'packages/lib/api/annotations/contract/task-group';
const { Paragraph, Text } = Typography;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getErrorStatusIfExists = (err: any) => {
  if (err.status) {
    return ` ${String(err.status)}${
      err.message ? ` ${String(err.message)}` : ''
    }`;
  }
  return '';
};

function getTotalTasksAvailable(
  taskGroups: TaskGroup[] | undefined,
  states: AbstractAnnotationStates
):
  | Array<{
      token: string;
      count: number;
    }>
  | undefined {
  // inProgress is any in-progress state, for example: "FlaggedInReview"
  const { initial, inProgress } = states;

  return taskGroups?.map((taskGroup) => {
    const toDoCount =
      taskGroup.stats?.[initial] ?? taskGroup.taskStats?.ToDo ?? 0;
    const inProgressCount =
      taskGroup.stats?.[inProgress] ?? taskGroup.taskStats?.InProgress ?? 0;
    const totalCount = toDoCount + inProgressCount;
    return {
      token: taskGroup.token,
      count: totalCount,
    };
  }, 0);
}

/**
 * Map workflow alias (tool name) to URL path for task
 */
export const toolAliasRegex =
  /(?<tool_alias>lidar-box-enrichment|lidar-box-amplification|lidar-box|lidar-top-down|lidar-seg|instance-seg|sem-seg|eva|audio-labeling|traffic-light-detection|classification-enrichment|scene-enrichment|data-mining-qa)/;
export const workflowToUrlTemplate: Record<
  AnnotationTool,
  (task: Task, sampleGroupToken?: string | null) => string
> = {
  'classification-enrichment': (task, sampleGroupToken) =>
    `/classification-enrichment?sample_group=${sampleGroupToken}&annotation_state=${task.annotationState}`,
  'scene-enrichment': (task, sampleGroupToken) =>
    `/scene-enrichment?sample_group=${sampleGroupToken}&annotation_state=${task.annotationState}`,
  'lidar-box': (task) =>
    `/lidar-box/${task.lidarExtractionToken}/${task.token}`,
  'lidar-seg': (task) =>
    `/lidar-seg/${task.lidarExtractionToken}/${task.token}`,
  'instance-seg': (task) =>
    `/instance-seg/${task.imageDataToken}/${task.token}`,
  'sem-seg': (task) => `/sem-seg/${task.imageDataToken}/${task.token}`,
  eva: (task) => `/eva/${task.videoExtractionToken}/${task.token}`,
  'traffic-light-detection': (task) =>
    `/traffic-light-detection/${task.videoExtractionToken}/${task.token}`,
  'audio-labeling': (task) =>
    `/audio-labeling/${task.audioExtractionToken}/${task.token}`,
  'lidar-top-down': (task) =>
    `/lidar-top-down/${task.lidarExtractionToken}/${task.token}`,
  'lidar-box-enrichment': (task) =>
    `/lidar-box-enrichment/${task.lidarExtractionToken}/${task.token}`,
  'lidar-box-amplification': (task) =>
    `/lidar-box-amplification/${task.lidarExtractionToken}/${task.token}`,
};

const DataWrapper: FunctionComponent<{
  workflow: AnnotationTool;
  labelingRole: string;
  pipeline: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  instructionDataSwr: SWRResponse<Instruction[], any, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sampleGroupDataSwr: SWRResponse<SampleGroup[], any, any>;
}> = ({
  workflow,
  labelingRole,
  pipeline,
  instructionDataSwr,
  sampleGroupDataSwr,
}) => {
  const { data: user } = useSWR('user-email', userEmail, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0, // disabled
  });
  const states = mapRoleToAnnotationStates(labelingRole as Role);
  const { initial, inProgress } = states;

  /**
   * ANCHOR - Fetch instruction data
   */
  const {
    data: instructionData,
    error: instructionDataError,
    isLoading: isLoadingInstructionData,
  } = instructionDataSwr;

  const instruction = instructionData?.[0];

  /**
   * ANCHOR - Fetch sample groups
   */
  const {
    data: sampleGroups,
    error: sampleGroupsError,
    isLoading: isLoadingSampleGroups,
  } = sampleGroupDataSwr;

  /**
   * ANCHOR - Fetch task groups
   * Required for fetching composing tasks into sample-group-based queueus
   */
  const {
    data: taskGroups,
    error: taskGroupsError,
    isLoading: isLoadingTaskGroups,
  } = useTaskGroupsForSampleGroups(sampleGroups, workflow);

  /**
   * ANCHOR - Fetch classification data for task queues
   * Required for fetching composing tasks into sample-group-based queueus
   */
  const {
    data: allClassificationData,
    error: classificationDataError,
    isLoading: isLoadingClassificationData,
  } = useSWR('all-classification-data', getAllClassificationData);

  const [totalTasksAvailable, setTotalTasksAvailable] = useState(
    getTotalTasksAvailable(taskGroups, states)
  );

  useEffect(() => {
    if (taskGroups) {
      setTotalTasksAvailable(getTotalTasksAvailable(taskGroups, states));
    }
  }, [states, states.initial, states.inProgress, taskGroups]);

  // Bugfix: handle edge case where only remaining tasks are assigned
  const setTaskGroupCount = useCallback(
    (taskGroupToken: string, count: number) => {
      setTotalTasksAvailable((prev) => {
        if (!prev) return undefined;
        if (prev.find((x) => x.token === taskGroupToken)) {
          return prev.map((x) => {
            if (x.token !== taskGroupToken) return x;
            return { ...x, count };
          });
        }
        return prev;
      });
    },
    []
  );

  const totalTaskCount = totalTasksAvailable?.reduce(
    (acc, d) => acc + d.count,
    0
  );

  /**
   * ANCHOR - Handle Case - Invalid pipeline provided in URL
   * In rare case that user mis-matches pipeline and workflow, show 404 page.
   */
  if (!instruction && !isLoadingInstructionData) {
    return (
      <Error404Result subTitle="Invalid pipeline, no Instruction data found" />
    );
  }

  /**
   * ANCHOR - Error handling UX
   * Handle case of API error and show clearly which request failed.
   */
  const hasError =
    sampleGroupsError ||
    taskGroupsError ||
    instructionDataError ||
    classificationDataError;

  if (hasError) {
    const iconStyle = { color: red[5] };
    const icon = <CloseCircleOutlined style={iconStyle} />;
    return (
      <Result
        status="error"
        title="An Error Occurred"
        style={{ height: '100%', marginTop: '20vh' }}
        subTitle="Something went wrong when trying to find tasks."
        extra={[
          <Link to="/contractor/tools" key="error-result-extra-content">
            <Button type="primary" size="large">
              Find a different task type
            </Button>
          </Link>,
        ]}
      >
        <div>
          <Paragraph>
            <Text
              strong
              style={{
                fontSize: 16,
              }}
            >
              The following errors occurred:
            </Text>
          </Paragraph>
          {instructionDataError && (
            <Paragraph>
              {icon} Failed to fetch instruction pipeline data.
              {getErrorStatusIfExists(instructionDataError)}
            </Paragraph>
          )}
          {sampleGroupsError && (
            <Paragraph>
              {icon} Failed to fetch sample groups.
              {getErrorStatusIfExists(sampleGroupsError)}
            </Paragraph>
          )}
          {taskGroupsError && (
            <Paragraph>
              {icon} Failed to fetch task groups.
              {getErrorStatusIfExists(taskGroupsError)}
            </Paragraph>
          )}
          {classificationDataError && (
            <Paragraph>
              {icon} Failed to fetch classification and taxonomy instruction
              data.{getErrorStatusIfExists(classificationDataError)}
            </Paragraph>
          )}
        </div>
      </Result>
    );
  }

  /**
   * ANCHOR - Empty queue UX
   * Handle case of empty task queues, give user easy option to return to
   * workflow selection portal.
   */
  const loadingSteps = [
    !isLoadingInstructionData,
    !isLoadingTaskGroups,
    !isLoadingSampleGroups,
    !isLoadingClassificationData,
  ];
  const loadingComplete = loadingSteps.every((x) => !!x);

  const allTasksComplete =
    totalTaskCount === 0 || (taskGroups && !taskGroups?.length);

  if (loadingComplete && allTasksComplete) {
    return (
      <Result
        status="success"
        style={{ height: '100%', marginTop: '20vh' }}
        title={
          <div style={{ marginTop: '3rem' }}>
            All tasks have been completed (or are assigned to other users).
          </div>
        }
        subTitle={
          <div
            style={{ marginBottom: '3rem' }}
          >{`It seems like all [${capitalCase(
            workflow
          )} ${pipeline} ${capitalCase(
            labelingRole
          )}] tasks have been completed. Please check back later.`}</div>
        }
        extra={
          <Link to="/contractor/tools">
            <Button type="primary" size="large">
              Find a different task type
            </Button>
          </Link>
        }
      />
    );
  }
  if (!user) return null;

  const showSkeletons =
    !loadingSteps.every((x) => !!x) || !sampleGroups || !taskGroups;

  return (
    <>
      <Typography.Title level={3}>
        Please choose a {capitalCase(labelingRole)} queue to launch a task:
      </Typography.Title>
      <div
        style={{
          background: customDefaultTheme.token?.colorPrimary ?? purple[5],
          width: 160,
          height: 4,
          margin: '1rem 0 2rem 0',
        }}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
        {showSkeletons ? (
          <Skeleton.Button active style={{ height: 256, width: 320 }} />
        ) : (
          <>
            {sampleGroups.map(({ name, token }) => {
              const elKey = `${name}-sample-group-queue`;
              const taskGroupsForSampleGroup = taskGroups.filter(
                (t) =>
                  t.sampleGroupToken === token &&
                  taskTypeToWorkflowAlias[t.type] === workflow
              );
              if (!taskGroupsForSampleGroup) return null;
              if (!name) return null;
              return taskGroupsForSampleGroup.map((taskGroup) => {
                const toDoCount =
                  taskGroup.stats?.[initial] ?? taskGroup.taskStats?.ToDo ?? 0;
                const inProgressCount =
                  taskGroup.stats?.[inProgress] ??
                  taskGroup.taskStats?.InProgress ??
                  0;
                const totalCount = toDoCount + inProgressCount;
                if (totalCount === 0) return null;
                return (
                  <QueueCard
                    key={`${elKey}-${taskGroup.name ?? taskGroup.token}`}
                    workflow={workflow}
                    sampleGroupToken={token}
                    taskGroupType={taskGroup.type}
                    labelingRole={labelingRole}
                    name={name}
                    user={user}
                    allClassificationData={allClassificationData}
                    toDoTaskCount={toDoCount}
                    setTaskGroupCount={setTaskGroupCount}
                  />
                );
              });
            })}
          </>
        )}
      </div>
    </>
  );
};

export const SampleGroupTaskQueuePortal: FunctionComponent<{
  workflow: AnnotationTool;
  labelingRole: string;
  pipeline: string;
}> = ({ workflow, labelingRole, pipeline }) => {
  /**
   * ANCHOR - Fetch instruction data
   */
  const instructionData = useSWR(`/instructions?name=${pipeline}`, (url) =>
    apiGet(url, instructionsRespZod.parse).catch((err) => {
      dispatchErrorToast(
        `Failed to fetch instructions by name: ${pipeline}. Please report this error and include browser console outputs.`
      );
      console.warn(`Failed to fetch instruction: ${pipeline}`);
      throw err;
    })
  );

  const instruction = instructionData.data?.[0];

  /**
   * ANCHOR - Fetch sample groups
   */
  const sampleGroupData = useSampleGroupsForInstruction(instruction?.token);
  return (
    <div style={{ padding: '2rem 3rem' }}>
      <Typography.Title>{`${capitalCase(pipeline)}`}</Typography.Title>
      <DataWrapper
        labelingRole={labelingRole}
        pipeline={pipeline}
        workflow={workflow}
        sampleGroupDataSwr={sampleGroupData}
        instructionDataSwr={instructionData}
      />
    </div>
  );
};

export default SampleGroupTaskQueuePortal;
